import { useRef, useState } from 'react';
import {
  planVersionContentSchema,
  type PlanVersion,
  type PlanVersionSummary,
} from '@traceio-org/platform-contracts';
import type { PlanVersionDraftCreateRequest } from '@traceio-org/platform-client';
import { RadioGroup, ReasonField, SelectField, TextField, reasonError } from '../../components/fields';
import { WriteDialog } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { PLAN_KIND_LABELS, productName, versionLabel } from '../../format';
import { ContentField, contentIssues, type ContentIssue } from './ContentField';
import {
  BILLED_PRODUCTS,
  PLAN_KEY,
  UUID,
  isBilledProductKey,
  plansOf,
  type BilledProductKey,
} from './shared';

type Mode = 'next' | 'new';
type ContentSource = 'copy' | 'json';
type PlanKind = PlanVersionSummary['planKind'];

const MODE_CHOICES = [
  {
    value: 'next',
    label: 'The next version of a plan',
    description: 'A new draft of a plan that exists, starting from a copy of one of its versions.',
  },
  {
    value: 'new',
    label: 'A new plan',
    description: 'Version 1 of a plan nobody has yet: a catalog plan, or a custom plan for one organization.',
  },
] as const;

const KIND_CHOICES = [
  { value: 'catalog', label: PLAN_KIND_LABELS.catalog!, description: 'Sold to any organization.' },
  { value: 'custom', label: PLAN_KIND_LABELS.custom!, description: "One organization's deal, signed by it alone." },
] as const;

const SOURCE_CHOICES = [
  { value: 'copy', label: 'Copy the content of a version', description: 'Edit it afterwards on the draft.' },
  {
    value: 'json',
    label: 'Paste the content as JSON',
    description: 'Checked against the plan version schema before it is sent.',
  },
] as const;

interface DraftForm {
  mode: Mode;
  planId: string;
  fromVersionId: string;
  key: string;
  name: string;
  productKey: BilledProductKey;
  kind: PlanKind;
  organizationId: string;
  source: ContentSource;
  json: string;
  reason: string;
}

type DraftErrors = Partial<Record<keyof DraftForm, string>>;

const EMPTY: DraftForm = {
  mode: 'next',
  planId: '',
  fromVersionId: '',
  key: '',
  name: '',
  productKey: 'enterprise',
  kind: 'catalog',
  organizationId: '',
  source: 'copy',
  json: '',
  reason: '',
};

export interface NewDraftDialogProps {
  open: boolean;
  onClose: () => void;
  /** The versions listed on the page (plans and copies are chosen among them); null while they load or failed. */
  versions: readonly PlanVersionSummary[] | null;
  /** Called with the draft platform-api created; the page then opens it. */
  onCreated: (version: PlanVersion) => void;
}

function versionOption(version: PlanVersionSummary): { value: string; label: string } {
  return {
    value: version.id,
    label: `${version.planName} ${versionLabel(version.version)} (${version.status})`,
  };
}

/**
 * A new draft: the next version of a plan from a copy of one of its versions, or version 1 of a new plan, from a
 * copy of a version of the same product or from content pasted as JSON. The JSON is checked against the contract's
 * schema here, with each issue at its path, and platform-api checks it again (its module checks need the
 * catalog): its issues come back at their paths too. A new draft opens as soon as platform-api created it.
 */
export function NewDraftDialog({ open, onClose, versions, onCreated }: NewDraftDialogProps) {
  const { api } = useAdmin();
  const [form, setForm] = useState<DraftForm>(EMPTY);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [issues, setIssues] = useState<ContentIssue[]>([]);
  const created = useRef<PlanVersion | null>(null);
  const set = <K extends keyof DraftForm>(field: K, value: DraftForm[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  const plans = plansOf(versions ?? []);
  const plan = plans.find((p) => p.planId === form.planId) ?? null;
  const copyCandidates =
    form.mode === 'next'
      ? (plan?.versions ?? [])
      : (versions ?? []).filter((v) => v.productKey === form.productKey);

  const planOptions = [
    { value: '', label: 'Choose a plan' },
    ...plans.map((p) => ({
      value: p.planId,
      label: `${p.planName} (${productName(p.productKey)}, ${PLAN_KIND_LABELS[p.planKind] ?? p.planKind})`,
    })),
  ];
  const copyOptions = [{ value: '', label: 'Choose a version' }, ...copyCandidates.map(versionOption)];
  const productOptions = BILLED_PRODUCTS.map((key) => ({ value: key, label: productName(key) }));

  const validate = (): PlanVersionDraftCreateRequest | null => {
    const found: DraftErrors = {};
    let content: unknown;
    if (form.mode === 'next') {
      if (!form.planId) found.planId = 'Choose a plan.';
      if (!form.fromVersionId) found.fromVersionId = 'Choose the version to copy.';
    } else {
      if (!PLAN_KEY.test(form.key.trim()))
        found.key = 'Lowercase letters, digits and hyphens, 1 to 64 characters, starting and ending with a letter or digit.';
      const name = form.name.trim();
      if (name.length < 2 || name.length > 120) found.name = 'Write a name of 2 to 120 characters.';
      if (form.kind === 'custom' && !UUID.test(form.organizationId.trim()))
        found.organizationId = 'Enter the organization ID (the organization page shows it).';
      if (form.source === 'copy') {
        if (!form.fromVersionId) found.fromVersionId = 'Choose the version to copy.';
      } else {
        const checked = contentIssues(form.json);
        setIssues(checked.issues);
        if (checked.issues.length > 0) found.json = checked.issues[0]!.message;
        content = checked.content;
      }
    }
    const reason = reasonError(form.reason);
    if (reason) found.reason = reason;
    setErrors(found);
    if (Object.keys(found).length > 0) return null;
    const planPart: PlanVersionDraftCreateRequest['plan'] =
      form.mode === 'next'
        ? { planId: form.planId }
        : {
            key: form.key.trim(),
            name: form.name.trim(),
            productKey: form.productKey,
            kind: form.kind,
            ...(form.kind === 'custom' ? { organizationId: form.organizationId.trim() } : {}),
          };
    if (form.mode === 'new' && form.source === 'json') {
      return { plan: planPart, content: planVersionContentSchema.parse(content), reason: form.reason };
    }
    return { plan: planPart, fromVersionId: form.fromVersionId, reason: form.reason };
  };

  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title="New draft"
      description="A draft is edited until it is published; a published version never changes."
      submitLabel="Create the draft"
      onSubmit={async () => {
        const request = validate();
        if (!request) return 'invalid';
        created.current = await api.createPlanVersionDraft(request);
        return undefined;
      }}
      onSaved={() => {
        if (created.current) onCreated(created.current);
        setForm(EMPTY);
        setErrors({});
        setIssues([]);
      }}
    >
      <RadioGroup
        legend="What to draft"
        choices={MODE_CHOICES}
        value={form.mode}
        onChange={(value) => {
          set('mode', value as Mode);
          set('fromVersionId', '');
        }}
      />
      {form.mode === 'next' ? (
        <>
          <SelectField
            label="Plan"
            value={form.planId}
            options={planOptions}
            onChange={(value) => {
              set('planId', value);
              // The newest version of the plan is the usual starting point.
              set('fromVersionId', plans.find((p) => p.planId === value)?.versions[0]?.id ?? '');
            }}
            error={errors.planId ?? null}
            hint={versions === null ? 'The plan list could not be loaded yet.' : undefined}
            required
          />
          <SelectField
            label="Copy the content of"
            value={form.fromVersionId}
            options={copyOptions}
            onChange={(value) => set('fromVersionId', value)}
            error={errors.fromVersionId ?? null}
            required
          />
        </>
      ) : (
        <>
          <TextField
            label="Plan key"
            value={form.key}
            onChange={(value) => set('key', value)}
            hint="Lowercase letters, digits and hyphens, for example trace-enterprise. It never changes."
            error={errors.key ?? null}
            spellCheck={false}
            maxLength={64}
            required
          />
          <TextField
            label="Plan name"
            value={form.name}
            onChange={(value) => set('name', value)}
            error={errors.name ?? null}
            maxLength={120}
            required
          />
          <SelectField
            label="Product"
            value={form.productKey}
            options={productOptions}
            onChange={(value) => {
              if (isBilledProductKey(value)) set('productKey', value);
              set('fromVersionId', '');
            }}
            required
          />
          <RadioGroup
            legend="Kind"
            choices={KIND_CHOICES}
            value={form.kind}
            onChange={(value) => set('kind', value as PlanKind)}
          />
          {form.kind === 'custom' ? (
            <TextField
              label="Organization ID"
              value={form.organizationId}
              onChange={(value) => set('organizationId', value)}
              hint="The organization this plan is for. Its page shows the ID."
              error={errors.organizationId ?? null}
              spellCheck={false}
              required
            />
          ) : null}
          <RadioGroup
            legend="Content"
            choices={SOURCE_CHOICES}
            value={form.source}
            onChange={(value) => set('source', value as ContentSource)}
          />
          {form.source === 'copy' ? (
            <SelectField
              label="Copy the content of"
              value={form.fromVersionId}
              options={copyOptions}
              onChange={(value) => set('fromVersionId', value)}
              hint={`Versions of ${productName(form.productKey)}, catalog and custom.`}
              error={errors.fromVersionId ?? null}
              required
            />
          ) : (
            <ContentField
              value={form.json}
              onChange={(value) => set('json', value)}
              error={errors.json ?? null}
              issues={issues}
            />
          )}
        </>
      )}
      <ReasonField value={form.reason} onChange={(value) => set('reason', value)} error={errors.reason ?? null} />
    </WriteDialog>
  );
}
